<template>
  <div class="form-group">
    <div class="input-icon form-floating">
      <input
        ref="textInput"
        type="text"
        placeholder="value"
        :id="elementId"
        @keypress="keyPress($event)"
        @mousedown="onMousedown()"
        @mouseup="onMouseup($event)"
        @focus="gotfocus($event)"
        @blur="lostfocus()"
        class="form-control"
        v-on:keyup.enter="onEnterPressed()"
        :readonly="isReadonly"
        :aria-label="ariaLabel"
      />
      <i>&pound;</i>
      <validation-label
        ref="label"
        :elementId="elementId"
        :text="description"
        :abbreviatedText="abbreviatedDescription"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { ref } from "vue";
import { inputValidator } from "../ts/inputValidator";
import { formatter } from "../ts/formatter";
import ValidationLabel from "../components/ValidationLabel.vue";

export default {
  name: "CurrencyInput",
  components: {
    ValidationLabel
  },
  props: {
    description: String,
    abbreviatedDescription: { default: "", required: false },
    elementId: String,
    minValue: { default: 1, required: false },
    maxValue: { type: Number, default: 1000000, required: false },
    width: { default: 100 },
    ariaLabel: { type: String, required: false }
  },
  setup() {
    const textInput = ref(null);
    const label = ref(null);

    return { textInput, inputValidator, formatter, label };
  },
  data() {
    return {
      receivedFocusFromMouse: false,
      readonly: false
    };
  },
  computed: {
    isReadonly: function() {
      return this.readonly;
    }
  },
  methods: {
    setValue(amount: number | null, readonly: boolean) {
      this.label.hideAnyWarnings();

      this.readonly = readonly;

      amount = this.getAmountTakingReadonlyStateIntoConsideration(
        amount,
        readonly
      );

      if (amount == null) {
        this.textInput.value = "";
      } else {
        this.textInput.value = formatter.formatCurrencyForInput(amount);
      }
    },
    getAmountTakingReadonlyStateIntoConsideration(
      value: number,
      readonly: boolean
    ): number | null {
      if (value != null && value != 0) {
        return value;
      }

      return readonly ? 0 : null;
    },
    lostfocus() {
      this.displayFormattedValue();
    },
    onEnterPressed() {
      this.displayFormattedValue();
      this.$emit("entered");
    },
    displayFormattedValue() {
      if (this.textInput === null) {
        return;
      }

      const integerValue = parseInt(this.textInput.value);
      if (isNaN(integerValue) || integerValue === undefined) {
        this.textInput.value = "";
        return;
      }

      this.textInput.value = formatter.formatCurrencyForInput(integerValue);
    },
    isFormatted() {
      return (this.textInput.value.match(/,/gi) || []).length > 0;
    },
    keyPress(event) {
      if (this.readonly) {
        event.preventDefault();
        return;
      }

      inputValidator.checkNumeric(event);
    },
    onMousedown() {
      this.receivedFocusFromMouse = true;
    },
    onMouseup(event) {
      this.receivedFocusFromMouse = false;

      if (!this.isFormatted()) {
        return;
      }

      const valueToLeftOfCursor = this.textInput.value.substring(
        0,
        event.target.selectionStart
      );
      const commaCount = (valueToLeftOfCursor.match(/,/gi) || []).length;
      const strippedCharCount = commaCount;
      const newCursorIndex = Math.max(
        0,
        event.target.selectionStart - strippedCharCount
      );

      this.displayNumericValue(event, newCursorIndex, newCursorIndex);
    },
    displayNumericValue(event, selectionStart: number, selectionEnd: number) {
      if (!this.isFormatted()) {
        return;
      }

      this.changing = true;

      if (this.canGetAmountAsInteger()) {
        this.textInput.value = this.getAmountAsInteger();
      } else {
        this.textInput.value = "";
      }

      if (event) {
        event.target.selectionStart = selectionStart;
        event.target.selectionEnd = selectionEnd;
      }
    },
    printCursorInfo(source, event) {
      console.log(
        `${source}`,
        `${event.target.selectionStart},${event.target.selectionEnd}`
      );
      const inputElement = document.getElementById(
        this.textInput.id
      ) as HTMLInputElement;
      console.log("\t element.selectionStart", inputElement.selectionStart);
    },
    gotfocus(event) {
      if (!this.receivedFocusFromMouse) {
        this.displayNumericValue(event, 0, 1000);
      }
    },
    getAmountAsInteger(): number {
      let integerValue = this.textInput.value;

      if (integerValue.length == 0 || integerValue == "£") {
        return 0;
      }

      integerValue = integerValue.replace(/,/gi, "").replace(/£/gi, "");
      return integerValue;
    },
    getValue(): number | null {
      if (this.canGetAmountAsInteger()) {
        return this.getAmountAsInteger();
      } else {
        return null;
      }
    },
    canGetAmountAsInteger(): boolean {
      let amount = this.textInput.value.trim();
      amount = amount.replace(/,/gi, "").replace(/£/gi, "");
      return this.inputValidator.isNumber(amount);
    },
    validate(): boolean {
      if (!this.canGetAmountAsInteger()) {
        this.label.warn();
        this.displayNumericValue();
        return false;
      }

      const amount: number = this.getAmountAsInteger();
      if (amount < this.minValue) {
        this.label.warn(`> ${formatter.formatCurrency(this.minValue)}`);
        this.displayNumericValue();
        return false;
      }

      if (amount >= this.maxValue) {
        this.label.warn(`< ${formatter.formatCurrency(this.maxValue)}`);
        this.displayNumericValue();
        return false;
      }

      this.label.hideAnyWarnings();

      return true;
    },
    hideAnyWarnings() {
      this.label.hideAnyWarnings();
    }
  }
};
</script>

<style scoped>
.input-icon {
  position: relative;
}

.input-icon > i {
  position: absolute;
  display: block;
  transform: translate(0, -22%);
  top: 53%;
  left: 7px;
  pointer-events: none;
  width: 25px;
  text-align: center;
  font-style: normal;
}

.input-icon > input[type="text"]:focus + i {
  display: block;
}

.input-icon > input {
  padding-left: 27px;
  padding-right: 0;
}

.form-floating > label {
  background-color: white;
  height: 96%;
  width: 99%;
}

.form-floating > .form-control:focus ~ label,
.form-floating > .form-control:not(:placeholder-shown) ~ label,
.form-floating > .form-select ~ label {
  background-color: transparent;
  height: 96%;
}

.form-floating > label {
  top: 1px;
  left: 1px;
  padding: 0.8rem 0.75rem;
  border-radius: 4px;
}
</style>
